import { MessageType, type Message } from './protocol'
import type { ConnectionState } from './types'

export interface ConnectionOptions {
  url: string
  autoReconnect?: boolean
  heartbeatInterval?: number
  reconnectInterval?: number
  heartbeatTimeout?: number
}

type ConnectionEvent = 'open' | 'close' | 'error' | 'message'
type ConnectionEventHandler = (...args: unknown[]) => void

export class Connection {
  private ws: WebSocket | null = null
  private url: string
  private autoReconnect: boolean
  private heartbeatInterval: number
  private reconnectInterval: number
  private heartbeatTimeout: number

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private heartbeatCheckTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private maxReconnectDelay = 30000

  private _state: ConnectionState = 'disconnected'
  private listeners = new Map<ConnectionEvent, Set<ConnectionEventHandler>>()
  private connectPromise: Promise<void> | null = null
  private pendingReject: ((reason: Error) => void) | null = null

  constructor(options: ConnectionOptions) {
    this.url = options.url
    this.autoReconnect = options.autoReconnect ?? true
    this.heartbeatInterval = options.heartbeatInterval ?? 25000
    this.reconnectInterval = options.reconnectInterval ?? 3000
    this.heartbeatTimeout = options.heartbeatTimeout ?? 60000
  }

  get state(): ConnectionState {
    return this._state
  }

  connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }
    // 复用在途连接 Promise：连接进行中重入调用时等待同一次连接的真实结果
    if (this.connectPromise) {
      return this.connectPromise
    }

    this._state = 'connecting'
    this.connectPromise = new Promise((resolve, reject) => {
      this.pendingReject = reject
      this.ws = new WebSocket(this.url)

      this.ws.onopen = () => {
        this._state = 'connected'
        this.reconnectAttempts = 0
        this.startHeartbeat()
        this.emit('open')
        this.settleConnect()
        resolve()
      }

      this.ws.onclose = (event) => {
        this._state = 'disconnected'
        this.stopHeartbeat()
        // 连接尚未建立即关闭：结束等待方，避免 await connect() 永久挂起
        if (this.connectPromise) {
          this.settleConnect()
          reject(new Error(`WebSocket closed before connected: ${event.code}`))
        }
        this.emit('close', event.code, event.reason)
        if (this.autoReconnect) {
          this.scheduleReconnect()
        }
      }

      this.ws.onerror = () => {
        this._state = 'disconnected'
        this.emit('error')
        if (this.connectPromise) {
          this.settleConnect()
          reject(new Error('WebSocket connection failed'))
        }
      }

      this.ws.onmessage = (event) => {
        this.resetHeartbeatCheck()
        try {
          const msg = JSON.parse(event.data as string) as Message
          // 心跳响应不触发 message 事件
          if (msg.type === MessageType.HEARTBEAT) return
          this.emit('message', msg)
        } catch {
          console.error('[SDK] Failed to parse WebSocket message')
        }
      }
    })
    return this.connectPromise
  }

  private settleConnect(): void {
    this.connectPromise = null
    this.pendingReject = null
  }

  disconnect(): void {
    this.stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    // 连接尚未建立即被主动断开：结束等待方
    if (this.connectPromise) {
      const reject = this.pendingReject
      this.settleConnect()
      reject?.(new Error('Connection closed before established'))
    }
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.close()
      this.ws = null
    }
    this._state = 'disconnected'
  }

  send(message: Message): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false
    }
    this.ws.send(JSON.stringify(message))
    return true
  }

  get isConnected(): boolean {
    return this._state === 'connected'
  }

  // ========== 心跳 ==========

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: MessageType.HEARTBEAT })
    }, this.heartbeatInterval)
    this.resetHeartbeatCheck()
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.heartbeatCheckTimer) {
      clearTimeout(this.heartbeatCheckTimer)
      this.heartbeatCheckTimer = null
    }
  }

  private resetHeartbeatCheck(): void {
    if (this.heartbeatCheckTimer) {
      clearTimeout(this.heartbeatCheckTimer)
    }
    this.heartbeatCheckTimer = setTimeout(() => {
      console.warn('[SDK] Heartbeat timeout, closing connection')
      this.ws?.close(4000, 'Heartbeat timeout')
    }, this.heartbeatTimeout)
  }

  // ========== 重连 ==========

  private scheduleReconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    const delay = Math.min(
      this.reconnectInterval * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    )
    this.reconnectAttempts++
    console.log(`[SDK] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)
    this.reconnectTimer = setTimeout(() => {
      this.connect().catch(() => {
        // connect 失败会触发 onclose，进而再次 scheduleReconnect
      })
    }, delay)
  }

  // ========== 事件 ==========

  on(event: ConnectionEvent, handler: ConnectionEventHandler): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
    return this
  }

  off(event: ConnectionEvent, handler: ConnectionEventHandler): this {
    this.listeners.get(event)?.delete(handler)
    return this
  }

  private emit(event: ConnectionEvent, ...args: unknown[]): void {
    const handlers = this.listeners.get(event)
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(...args)
        } catch (e) {
          console.error(`[SDK] Error in connection ${event} handler:`, e)
        }
      }
    }
  }
}
